import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import EnterpriseCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseCourseEnrollButtonNotConfigurable';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import Description from 'bundles/unified-description-page-common/components/modules/product-overview/Description';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getEnterpriseProjectKeyInformationItems } from 'bundles/unified-description-page-common/utils/getEnterpriseKeyInformationItems';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductDetailItems from 'bundles/unified-description-page-common/utils/getProductDetailItems';
import {
  getDescriptionTitle,
  getSkillsTitle,
} from 'bundles/unified-description-page-common/utils/getSectionTitlesForProject';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: EnterpriseCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const ProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners);
  const { navigationLinks } = usePageNavigation();
  const { pageType, navigationIdMap } = usePageInfo();
  const features = useEnterpriseProductFeatures();
  const buttonLabel = pageType === PAGE_TYPES.GUIDED_PROJECT ? _t('Go To Guided Project') : _t('Go To Project');
  const { programSlug } = useParams();

  const overviewSkills = (
    <Skills skills={pageData.skills} title={getSkillsTitle(pageType)} disableCTA={config?.removeLinksAboveFold} />
  );
  const overviewDescription = pageData.cmlLearningObjectives ? (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} title={getDescriptionTitle(pageType)} />
  ) : (
    <Description description={pageData.description} productId={pageData.id} title={getDescriptionTitle(pageType)} />
  );
  const details = <Details detailItems={getProductDetailItems(pageData, config?.productDetailItems || [], pageType)} />;

  const { items: keyInformationItems } = getEnterpriseProjectKeyInformationItems(
    pageData,
    {
      ...config?.keyInfoModule,
    },
    config?.removeLinksAboveFold,
    pageType
  );

  return (
    <ProductOverviewHorizontalModule
      anchorId={navigationIdMap?.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModule
          productTitle={productTitle}
          backgroundImage={config?.backgroundImage}
          partnerLogo={
            partnersToDisplay && partnersToDisplay.length > 1 ? (
              <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={false} />
            ) : (
              <HeroPartnerLogo image={partnersToDisplay?.[0]?.primaryLogo} partnerName={partnersToDisplay?.[0]?.name} />
            )
          }
          description={
            config?.showHeroLanguageInfo && (
              <HeroLanguageInfo
                languages={{
                  primaryLanguages: pageData.primaryLanguages,
                  subtitleLanguages: pageData.subtitleLanguages ?? [],
                  translatedLanguages: pageData.translatedLanguages ?? [],
                  dubbedLanguages: pageData.dubbedLanguages ?? [],
                }}
              />
            )
          }
          instructors={
            !config?.hideInstructors && (
              <HeroInstructors
                instructors={pageData.instructors}
                averageInstructorRating={pageData.averageInstructorRating}
                instructorRatingCount={pageData.instructorRatingCount}
                partners={pageData.partners}
              />
            )
          }
          primaryCta={
            <EnterpriseCourseEnrollButtonNotConfigurable
              courseType={pageData.courseType}
              productId={pageData.id}
              slug={pageData.slug}
              shouldShowSponsoredByMessage={features?.shouldShowSponsoredByMessage}
              buttonLabel={buttonLabel}
            />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable
                productSlug={pageData.slug}
                productType="project"
                programSlug={programSlug}
              />
            )
          }
          additionalInfo={<HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />}
        />
      }
      keyInformation={
        <KeyInformationHorizontalModule
          keyInformationItems={keyInformationItems}
          applyLightShadow={!!config?.heroModule?.backgroundColor}
        />
      }
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={overviewDescription}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default ProductOverviewModuleRenderer;
